import type { ReactNode } from 'react';
import { juntarClasses } from './classes';
import { IconeCaixaVazia } from './icones';
import { useRevelacao } from './movimento';

export interface PropsEstadoVazio {
  /** Frase curta e concreta: "Nenhum contrato em 2026". */
  titulo: string;
  /**
   * O que fazer a seguir, ou por que está vazio. Sem isto o usuário não
   * distingue "não existe" de "falhou em carregar".
   */
  descricao?: string;
  /** Ação principal — normalmente um `Botao`. */
  acao?: ReactNode;
  icone?: ReactNode;
  compacto?: boolean;
  /** `false` desenha sem moldura, para usar dentro de um cartão que já tem borda. */
  comMoldura?: boolean;
  className?: string;
}

/**
 * Estado vazio.
 *
 * §8 é explícito: nunca tela preta. Uma lista sem resultados precisa dizer o que
 * está vazio, por quê e qual é o próximo passo — no legado, a mesma tela em
 * branco servia para "sem dados", "sem permissão" e "deu erro", e o operador não
 * tinha como distinguir.
 */
export function EstadoVazio({
  titulo,
  descricao,
  acao,
  icone,
  compacto = false,
  comMoldura = true,
  className,
}: PropsEstadoVazio): JSX.Element {
  const referencia = useRevelacao<HTMLDivElement>({ distancia: 8 });

  return (
    <div
      ref={referencia}
      className={juntarClasses(
        'flex flex-col items-center justify-center text-center',
        compacto ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14',
        comMoldura && 'rounded-painel border border-dashed border-borda bg-superficie-elevada/40',
        className,
      )}
    >
      <span
        className={juntarClasses(
          'flex items-center justify-center rounded-full bg-superficie-vidro text-conteudo-secundario',
          compacto ? 'h-10 w-10' : 'h-14 w-14',
        )}
      >
        {icone ?? <IconeCaixaVazia className={compacto ? 'h-5 w-5' : 'h-6 w-6'} />}
      </span>

      <h3 className="font-display text-base font-semibold tracking-tight text-conteudo-primario">
        {titulo}
      </h3>

      {descricao !== undefined && (
        <p className="max-w-md text-sm leading-relaxed text-conteudo-secundario">{descricao}</p>
      )}

      {acao !== undefined && <div className="mt-2">{acao}</div>}
    </div>
  );
}
